import { rayEnd } from "./drawingRay";
import { polygonTraps } from "./drawingShapeParts";

const BASE_LEVEL = 1;
const MEDIAN_LEVEL = 0;

const offsetFrom = (middle, side, value) => ({ x: middle.x + side.x * value, y: middle.y + side.y * value });

const rayFrom = (from, direction, pane) => {
  const end = rayEnd(from, { x: from.x + direction.x, y: from.y + direction.y }, pane);

  return [from.x, from.y, end.x, end.y];
};

// Past any place of the pane from both points: the rays of a band stay parallel, the clip cuts them
const farPastPane = (from, direction, reach) => {
  const ratio = reach / Math.hypot(direction.x, direction.y);

  return { x: from.x + direction.x * ratio, y: from.y + direction.y * ratio };
};

const reachOver = ({ width, height }, ...places) =>
  width + height + Math.max(...places.map(({ x, y }) => Math.abs(x) + Math.abs(y)));

/**
 * Andrew's Pitchfork (po-chart-visual-spec.md §7.5): the median from the first
 * point through the middle M of the other two; per level two lines parallel to it from
 * M ± (second point − M) × level; all without an end. The base through M reaches
 * the widest level: between the second and the third point in the colour of the
 * median, further on each part in the colour of its level. A band between neighbouring
 * levels, and from the median to the first one, has the colour of the outer level. See TOOLS in drawingTools.js.
 *
 * @param {Array<{ x: number, y: number }>} points - CSS px of the pane
 * @param {{ width: number, height: number }} pane
 * @param {object} style - PITCHFORK_STYLE with colour and width
 * @returns {{ strokes: object[], fills: object[] }}
 */
export const pitchforkShape = ([pivot, second, third], pane, style) => {
  const middle = { x: (second.x + third.x) / 2, y: (second.y + third.y) / 2 };
  const direction = { x: middle.x - pivot.x, y: middle.y - pivot.y };
  const hasDirection = direction.x !== 0 || direction.y !== 0;
  const sides = [
    { x: second.x - middle.x, y: second.y - middle.y },
    { x: third.x - middle.x, y: third.y - middle.y },
  ];
  const levels = style.levels
    .filter(({ isVisible }) => isVisible)
    .sort((first, next) => first.value - next.value);
  const placesOf = (value) => sides.map((side) => offsetFrom(middle, side, value));

  const levelStrokes = levels.map(({ value, color, width }) => ({
    segments: placesOf(value).map((place) => rayFrom(place, direction, pane)),
    color,
    width,
  }));

  const outerLevels = levels.filter(({ value }) => value > BASE_LEVEL);
  const baseParts = outerLevels.map(({ value, color, width }, index) => {
    const innerValue = index === 0 ? BASE_LEVEL : outerLevels[index - 1].value;
    const inner = placesOf(innerValue);

    return {
      segments: placesOf(value).map((place, side) => [inner[side].x, inner[side].y, place.x, place.y]),
      color,
      width,
    };
  });

  const bandOf = ({ value, color }, index) => {
    const inner = placesOf(index === 0 ? MEDIAN_LEVEL : levels[index - 1].value);
    const traps = placesOf(value).flatMap((outer, side) => {
      const reach = reachOver(pane, outer, inner[side]);

      return polygonTraps([
        inner[side],
        outer,
        farPastPane(outer, direction, reach),
        farPastPane(inner[side], direction, reach),
      ]);
    });

    return { color, traps };
  };

  return {
    strokes: [
      ...(style.isMedianVisible ? [{ segments: [rayFrom(pivot, direction, pane)], color: style.color, width: style.width }] : []),
      ...levelStrokes,
      { segments: [[second.x, second.y, third.x, third.y]], color: style.color, width: style.width },
      ...baseParts,
    ],
    fills: style.isBackgroundVisible && hasDirection ? levels.map(bandOf) : [],
  };
};
